import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, PenLine, Type } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/proposals/SignaturePad";
import { cn } from "@/lib/utils";

const SCRIPT_FONT = "'Segoe Script', 'Brush Script MT', 'Snell Roundhand', cursive";

export interface SigningValues {
  signer_name: string;
  signer_title?: string;
  signature_type: "TYPED" | "DRAWN";
  signature_image?: string;
  agree: true;
  otp?: string;
}

/**
 * The signing box shared by the customer's page and the company's countersignature: name, a typed or drawn
 * signature, the statement being agreed to, and (when the company asks for it) the emailed one-time code.
 */
export function SigningDialog({
  open,
  onOpenChange,
  heading,
  description,
  defaultName,
  nameLocked,
  defaultTitle,
  statement,
  otp,
  submitLabel = "Sign",
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  heading: string;
  description?: string;
  defaultName?: string;
  /** The signer is who they are (the signed-in user countersigning); the name can't be changed */
  nameLocked?: boolean;
  defaultTitle?: string;
  /** The statement shown before signing, with "[your name]" filled in as they type */
  statement: string;
  /** When set, a code must be entered; `request` emails it */
  otp?: { request: () => Promise<{ sent_to?: string | null } | void> };
  submitLabel?: string;
  onSubmit: (v: SigningValues) => Promise<void>;
}) {
  const [name, setName] = useState(defaultName || "");
  const [title, setTitle] = useState(defaultTitle || "");
  const [sigType, setSigType] = useState<"TYPED" | "DRAWN">("TYPED");
  const [drawn, setDrawn] = useState<string | null>(null);
  const [agree, setAgree] = useState(false);
  const [code, setCode] = useState("");
  const [codeSentTo, setCodeSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(defaultName || "");
    setTitle(defaultTitle || "");
    setAgree(false);
    setCode("");
    setCodeSentTo(null);
    setProblem("");
  }, [open, defaultName, defaultTitle]);

  const text = statement.replace("[your name]", name.trim() || "[your name]").replace(/\b(typed|drawn)\b signature/, `${sigType === "DRAWN" ? "drawn" : "typed"} signature`);
  const input = "h-10 w-full rounded-lg border border-border bg-white px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-muted";

  const requestCode = async () => {
    setProblem("");
    setBusy(true);
    try {
      const r = await otp!.request();
      setCodeSentTo((r && (r as any).sent_to) || "your email");
    } catch (err: any) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProblem("");
    if (name.trim().length < 2) return setProblem("Enter your full name.");
    if (sigType === "DRAWN" && !drawn) return setProblem("Draw your signature in the box.");
    if (otp && !/^\d{6}$/.test(code.trim())) return setProblem("Enter the 6-digit code we emailed you.");
    if (!agree) return setProblem("Tick the box to confirm.");
    setBusy(true);
    try {
      await onSubmit({
        signer_name: name.trim(),
        signer_title: title.trim() || undefined,
        signature_type: sigType,
        signature_image: sigType === "DRAWN" ? drawn! : undefined,
        agree: true,
        otp: otp ? code.trim() : undefined,
      });
    } catch (err: any) {
      setProblem(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white text-foreground">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          {description && <DialogDescription className="text-muted-foreground">{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="space-y-1 text-sm font-medium text-foreground">
              Full name *
              <input className={input} value={name} disabled={nameLocked} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </label>
            <label className="space-y-1 text-sm font-medium text-foreground">
              Job title
              <input className={input} value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} autoComplete="organization-title" />
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex gap-1 rounded-lg bg-muted p-1 text-xs font-semibold">
              {(
                [
                  ["TYPED", "Type", Type],
                  ["DRAWN", "Draw", PenLine],
                ] as const
              ).map(([id, label, Icon]) => (
                <button key={id} type="button" onClick={() => setSigType(id)} className={cn("flex-1 h-8 rounded-md inline-flex items-center justify-center gap-1.5", sigType === id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground")}>
                  <Icon className="size-3.5" /> {label} signature
                </button>
              ))}
            </div>
            {sigType === "TYPED" ? (
              <div className="h-[100px] rounded-lg border-2 border-dashed border-border bg-white flex items-center justify-center px-4">
                <span style={{ fontFamily: SCRIPT_FONT }} className="text-3xl text-primary truncate">
                  {name.trim() || <span className="text-base text-muted-foreground font-sans">Your name appears here</span>}
                </span>
              </div>
            ) : (
              <SignaturePad onChange={setDrawn} height={140} />
            )}
          </div>

          {otp && (
            <div className="rounded-lg border p-3 space-y-2">
              <div className="text-sm font-medium text-foreground">Confirm it's you</div>
              <div className="flex gap-2">
                <input className={cn(input, "tracking-[0.3em] text-center font-mono")} inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoComplete="one-time-code" />
                <button type="button" onClick={requestCode} disabled={busy} className="h-10 px-3 shrink-0 rounded-lg border border-border bg-white text-sm font-medium text-foreground hover:bg-muted disabled:opacity-70">
                  {codeSentTo ? "Send again" : "Email me a code"}
                </button>
              </div>
              {codeSentTo && <p className="text-xs text-muted-foreground">We sent a code to {codeSentTo}. It works for 10 minutes.</p>}
            </div>
          )}

          <div className="rounded-lg bg-muted border p-3 text-xs text-foreground whitespace-pre-line max-h-40 overflow-y-auto">{text}</div>
          <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="size-4 mt-0.5 accent-success" />
            I agree to the statement above, and to signing electronically.
          </label>
          {problem && <p className="text-sm text-danger">{problem}</p>}
          <DialogFooter>
            <button type="button" disabled={busy} onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-lg border border-border text-sm font-semibold text-foreground">
              Cancel
            </button>
            <button type="submit" disabled={busy} className="h-10 px-5 rounded-lg bg-success text-white text-sm font-semibold hover:bg-success/90 inline-flex items-center justify-center gap-1.5 disabled:opacity-70">
              {busy ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} {submitLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
